import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { Phone, Mail, MapPin, Clock, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ScrollReveal } from "./ScrollReveal";
import Header from "./Header";
import Footer from "./Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`/.netlify/functions/send-email`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        goal: formData.service,
        page_source: "contact-page",
        message: formData.message.trim(),
        timestamp: new Date().toISOString(),
      });

      navigate(
        `/thank-you?service=contact-page&name=${encodeURIComponent(formData.name)}`,
      );
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Please try again or call us directly at 918.477.7227",
      });
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      primary: "918.477.7227",
      secondary: "Mon-Fri, 8am-6pm CST",
      href: "tel:+19184777227",
    },
    {
      icon: Mail,
      title: "Email",
      primary: "curt@summitinvesting.com",
      secondary: "We respond within 24 hours",
      href: "mailto:curt@summitinvesting.com",
    },
    {
      icon: MapPin,
      title: "Office",
      primary: "10505 S. 68th E Ave",
      secondary: "Tulsa, OK 74133",
      href: "https://maps.google.com/?q=10505+S+68th+E+Ave+Tulsa+OK+74133",
    },
    {
      icon: Clock,
      title: "Business Hours",
      primary: "Monday - Friday",
      secondary: "8:00 AM - 6:00 PM CST",
      href: null,
    },
  ];

  const services = [
    "Retirement Income Planning",
    "Wealth Management",
    "Investment Management",
    "Portfolio Management",
    "Legacy Planning",
    "Tax Efficient Strategies",
    "Insurance",
    "Business Strategies",
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="pt-28 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#f8fafb] to-white"
        data-testid="contact-hero"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="hero-headline text-4xl sm:text-5xl font-bold text-[#3d4f5f] mb-6">
            Let's Start a Conversation
          </h1>
          <p className="hero-subheadline text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Ready to take control of your financial future? Our team of
            experienced advisors is here to help you every step of the way.
          </p>
        </div>
      </section>

      <section className="pb-6">
        <img
          src="/man-cropped.jpg"
          alt="Man"
          loading="eager"
          className="shadow-lg"
          style={{
            maxWidth: "400px",
            height: "auto",
            margin: "0 auto",
            borderRadius: "24px",
          }}
        />
      </section>

      {/* Contact Cards */}
      <ScrollReveal>
        <section
          className="py-12 px-4 sm:px-6 lg:px-8 bg-white"
          data-testid="contact-info-section"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactInfo.map((info, index) => (
                <Card
                  key={index}
                  className="card-hover p-6 text-center border-2 border-gray-100 hover:border-[#3d4f5f] transition-all"
                  data-testid={`contact-card-${index}`}
                >
                  <div className="w-12 h-12 bg-[#e8eef2] rounded-full flex items-center justify-center mx-auto mb-4">
                    <info.icon className="w-6 h-6 text-[#3d4f5f]" />
                  </div>
                  <h3 className="font-semibold text-[#3d4f5f] mb-2">
                    {info.title}
                  </h3>
                  {info.href ? (
                    <a
                      href={info.href}
                      className="text-gray-800 hover:text-[#3d4f5f] font-medium block transition-colors"
                      target={
                        info.href.startsWith("http") ? "_blank" : undefined
                      }
                      rel={
                        info.href.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      {info.primary}
                    </a>
                  ) : (
                    <p className="text-gray-800 font-medium">{info.primary}</p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">{info.secondary}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Contact Form & Map Section */}
      <section
        className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8fafb]"
        data-testid="contact-form-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#3d4f5f] mb-6">
                Send Us a Message
              </h2>
              <Card
                className="p-8 bg-white shadow-lg border border-gray-100"
                data-testid="contact-form"
              >
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                        placeholder="John Smith"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                        placeholder="(405) 555-1234"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Service of Interest *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white text-gray-700 focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                      data-testid="select-service"
                    >
                      <option value="">Select a service...</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg resize-none focus:border-[#3d4f5f] focus:ring-2 focus:ring-[#3d4f5f]/20 transition-all"
                      placeholder="Tell us about your financial goals or questions..."
                      data-testid="input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full bg-[#4a6274] hover:bg-[#3a5263] text-white py-4 text-base font-semibold rounded-lg shadow-lg disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 text-center">
                    By submitting, you agree to be contacted about our financial
                    services. No obligation.
                  </p>
                </form>
              </Card>
            </div>

            {/* Map & Additional Info */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#3d4f5f] mb-6">
                Visit Our Office
              </h2>
              <Card
                className="overflow-hidden border border-gray-100 shadow-lg mb-6"
                data-testid="map-card"
              >
                <div className="h-64 bg-gray-200">
                  <iframe
                    title="Office Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3224.0!2d-95.9!3d36.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDAzJzAwLjAiTiA5NcKwNTQnMDAuMCJX!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="font-semibold text-[#3d4f5f] mb-2">
                    Summit Investment Group
                  </h3>
                  <p className="text-gray-600 text-sm">
                    10505 S. 68th E Ave
                    <br />
                    Tulsa, OK 74133
                  </p>
                  <a
                    href="https://maps.google.com/?q=10505+S+68th+E+Ave+Tulsa+OK+74133"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-[#3d4f5f] hover:text-[#2d3e4e] font-medium text-sm transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    Get Directions
                  </a>
                </div>
              </Card>

              {/* Why Contact Us */}
              <Card
                className="p-6 bg-white border border-gray-100"
                data-testid="why-contact-card"
              >
                <h3 className="font-semibold text-[#3d4f5f] mb-4">
                  What to Expect
                </h3>
                <ul className="space-y-3">
                  {[
                    "Free, no-obligation consultation",
                    "Response within 24 business hours",
                    "Personalized financial assessment",
                    "Clear explanation of our services",
                    "No pressure, just honest guidance",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#4a6274] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
